import React from 'react';
import { useTheme } from '@veneer/theme';
import { css, SerializedStyles } from '@emotion/react';
import focusRing from '../utils/focus_ring';

const useStyles = (): Record<
  | 'root'
  | 'interactive'
  | 'contrast'
  | 'contrastAndInteractive'
  | 'filled'
  | 'outlined'
  | 'shadowed'
  | 'pill',
  SerializedStyles
> => {
  const theme = useTheme();

  return React.useMemo(() => {
    return {
      root: css({
        // Resets for button
        lineHeight: 'unset',
        padding: 'unset',
        textAlign: 'unset',
        fontSize: 'unset',
        // Resets for link
        textDecoration: 'unset',
        position: 'relative',
        display: 'block',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        borderRadius: theme.borderRadius5,
        borderStyle: 'solid',
        borderWidth: '1px',
        boxSizing: 'inherit',
        color: theme.color.foreground.default,
        wordBreak: 'break-word',
        ...focusRing(theme, 2, -4),
      }),

      interactive: css({
        cursor: 'pointer',

        // Delegate focus-visible ring to card for any contained inputs
        'input:is([type="radio"], [type="checkbox"]):focus-visible ~ *': {
          outline: 'none',
        },

        ':has(input:is([type="radio"], [type="checkbox"]):focus-visible)': {
          ...focusRing(theme, 2, -4)[':focus-visible'],
        },

        ':hover': {
          color: 'unset',
          textDecoration: 'unset',
          borderColor: theme.color.stroke.informative.hover,
        },

        ':active': {
          borderColor: theme.color.stroke.informative.active,
        },
      }),

      contrast: css({
        borderColor: theme.color.foreground.default,
      }),

      contrastAndInteractive: css({
        borderColor: theme.color.primary.base.default,
      }),

      filled: css({
        backgroundColor: theme.color.background.container.default,
      }),

      outlined: css({
        borderColor: theme.color.stroke.decorative.default,
      }),

      shadowed: css({
        boxShadow: `${theme.elevation.level4.boxShadow} ${theme.color.effects.dropShadow}`,
      }),

      pill: css({
        borderRadius: theme.borderRadius10,
      }),
    };
  }, [theme]);
};

export default useStyles;
